# MultipleChoiceGame.vue
<script setup>
import { ref, computed, inject, onMounted } from 'vue'
import { getBaseUrl } from '@/helpers/fetch-wrapper'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth.store'
const { user } = useAuthStore()
const { t } = useI18n()
const audiomanager = inject('audiomanager')
import { useTipStore } from '@/stores/tip.store'
const tipStore = useTipStore()

const props = defineProps({
  gameState: {
    type: Object,
    required: true
  },
  enable_audio: {
    type: Boolean,
  },
  // we don't allow skipping in the registration game to save space
  allowSkip: {
    type: Boolean,
    default: true
  },
  // currently just for use in the registration game, we make the buttons smaller like mobile
  compact: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['game-action', 'enable-audio', 'toggle-audio', 'chat-with-tutor', 'example-sentences', 'skip-concept'])

function enableActions() {
  console.log('enableClick called')
  // make this find any button with class choicebutton and enable all of them
  const buttons = document.querySelectorAll('.choicebutton')
  buttons.forEach(button => {
    button.style.pointerEvents = 'auto'
  })
}

onMounted(() => {
  console.log('add body no hover')
    setTimeout(() => {
      console.log('remove body no hover')
      enableActions()
    }, 1000); // adjust timing as needed
})

// Local UI state
const hasAnswered = ref(false)
const selectedAnswer = ref(null)
const showControls = ref(false)

// Computed properties
const currentConcept = computed(() => props.gameState.current_concept)

const questionWordDisplay = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  return props.gameState.direction === 'native-to-learning'
    ? concept.native.text
    : concept.learning.annotated.annotation
})

const questionWord = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  return props.gameState.direction === 'native-to-learning'
    ? concept.native.text
    : concept.learning.text
})

const correctAnswerDisplay = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  return props.gameState.direction === 'native-to-learning'
    ? concept.learning.annotated.annotation
    : concept.native.text
})

const correctAnswer = computed(() => {
  const concept = currentConcept.value
  if (!concept?.native) return ''
  return props.gameState.direction === 'native-to-learning'
    ? concept.learning.text
    : concept.native.text
})

const options = ref([])

const concept = currentConcept.value

const distractors = props.gameState.direction === 'native-to-learning'
  ? concept.learning.distractors
  : concept.native.distractors

// Get correct answer and 3 random distractors
const answer = correctAnswer.value
const randomDistractors = distractors
  .sort(() => Math.random() - 0.5)
  .slice(0, 3)

// Combine and shuffle
const answerOption = {
  text: correctAnswer.value,
  annotation: correctAnswerDisplay.value
}
options.value = [answerOption, ...randomDistractors]
  .sort(() => Math.random() - 0.5)

const questionLanguage = computed(() => {
  return props.gameState.direction === 'native-to-learning'
    ? 'native'
    : 'learning'
})

const answerLanguage = computed(() => {
  return props.gameState.direction === 'native-to-learning'
    ? 'learning'
    : 'native'
})

function getAudioUrl() {
  return getBaseUrl(`/tts/?lang=${concept.learning.language}&text=${concept.learning.pronunciation}&token=${user.token}`)
}
audiomanager.preload(getAudioUrl())

// Play the audio of the learning language option
const playAudio = (slow = false) => {
  const url = getAudioUrl()
  audiomanager.setPlaybackSpeed(slow ? 0.50 : 1) // Set playback speed to 0.50 for slow, 1 for normal
  audiomanager.play(url)
}

if (props.gameState.direction == 'learning-to-native' && props.gameState.current_concept?.concept_type != 'CHARACTER') {
  if (props.enable_audio) {
    const url = getAudioUrl()
    audiomanager.setPlaybackSpeed(1)
    audiomanager.play(url)
  }
}

const buttonClass = (option) => {
  let base_classes = ''
  if (!hasAnswered.value) base_classes = 'btn-outline-primary-cs'
  else if (hasAnswered.value && option.text === correctAnswer.value) base_classes = 'btn-success'
  else if (hasAnswered.value && option.text === selectedAnswer.value && option.text !== correctAnswer.value) base_classes = 'btn-danger'
  else if (hasAnswered.value) {
    base_classes = 'd-none'
  }
  else {
    base_classes = 'btn-outline-primary-cs'
  }
  if (props.compact) {
    return `${base_classes} btn-xs compact-btn-xs`
  }
  else {
    return `${base_classes} btn-xs`
  }
}

const rowClass = (option) => {
  if (!hasAnswered.value) return 'col-sm-12'
  if (hasAnswered.value && option.text === correctAnswer.value) return 'col-12'
  if (hasAnswered.value && option.text === selectedAnswer.value && option.text !== correctAnswer.value) return 'col-12'
  if (hasAnswered.value) {
    return 'd-none'
  }
  return ''
}

// Methods
async function checkAnswer(selected) {
  if (hasAnswered.value) return

  selectedAnswer.value = selected.text
  hasAnswered.value = true

  // Emit game action
  console.log("selected", selectedAnswer.value)
  console.log("correctAnswer", correctAnswer.value)
  console.log("correct", selectedAnswer.value === correctAnswer.value)
  let audioUrl = null
  if (props.gameState.direction == 'native-to-learning') {
    if (props.enable_audio) {
      audioUrl = getBaseUrl(`/tts/?lang=${concept.learning.language}&text=${concept.learning.pronunciation}&token=${user.token}`)
    }
  }
  const result = await emit('game-action', {
    selected: selectedAnswer.value,
    expected: correctAnswer.value,
    correct: selectedAnswer.value === correctAnswer.value,
    audioUrl: audioUrl,
  })
  console.log("result", result)

  // If result indicates game is complete, reset local state
  if (result?.game_state?.completed) {
    hasAnswered.value = false
    selectedAnswer.value = null
  }
}

async function toggleAudio() {
  emit('toggle-audio')
}

function chatWithTutor() {
  showControls.value = false
  emit('chat-with-tutor')
}

function showExampleSentences() {
  showControls.value = false
  emit('example-sentences')
}

</script>

<template>
  <div class="game-container border p-3 shadow rounded-4 mt-2">
    <div class="word-to-translate mb-2">
        <div class="me-0" style="float: right">
          <button
            type="button"
            class="btn-grear"
            @click="showControls = !showControls">
            <i :class="showControls ? 'bi bi-gear' : 'bi bi-gear'"></i>
          </button>
          <div v-if="showControls" class="popout-menu">
            <ul class="text-start">
              <li @click="chatWithTutor()">
                <i class="bi bi-chat-dots me-1"></i>
                {{ t('course.chat-with-tutor') }}
              </li>
              <li @click="showExampleSentences()">
                <i class="bi bi-translate me-1"></i>
                {{ t('curriculum.example-sentences') }}
              </li>
              <li v-if="props.gameState.vocab_entry_id" @click="router.push({ name: 'EditVocab', params: { id: props.gameState.vocab_entry_id } })" >
                <i class="bi bi-pencil-square me-1"></i>
                {{ t('curriculum.edit-vocab') }}
              </li>
              <li v-if="canUndo" class="d-flex align-items-center" @click="undoLastReview">
                <i class="bi bi-arrow-counterclockwise me-1"></i>
                {{ t('game.undo') }}
              </li>
              <li @click="toggleAudio()" >
                <i :class="props.enable_audio ? 'bi bi-volume-up-fill me-1' : 'bi bi-volume-mute-fill me-1'"></i>
                {{ props.enable_audio ? t('game.audio-on') : t('game.audio-off') }}
              </li>
              <li @click="playAudio(false)" >
                <i class="bi bi-play-circle me-1"></i>
                {{ t('curriculum.play-again') }}
              </li>
              <li @click="playAudio(true)" >
                <i class="bi bi-play-circle me-1"></i>
                {{ t('curriculum.play-slow') }}
              </li>
              <li v-if="debug" @click="hideAnswer">
                <i class="bi bi-eye-slash me-1"></i>
                Hide answer
              </li>
            </ul>
          </div>
        </div>
      <h2 class="mb-2" v-html="questionWordDisplay"></h2>
    </div>

    <div class="audio-controls mb-4 " v-if="hasAnswered">
      <button type="button" class="btn btn-outline-primary btn-outline-primary-cs mb-1 ms-1" @click="playAudio(false)">
        <i class="bi bi-volume-up-fill  me-2"></i> {{ t('curriculum.play-again') }}
      </button>
      <button type="button" class="btn btn-outline-primary btn-outline-primary-cs mb-1 ms-1" @click="playAudio(true)">
        <i class="bi bi-volume-down-fill  me-2"></i> {{ t('curriculum.play-slow') }}
      </button>
    </div>

    <div class="options-grid">
      <div class="row g-3">
        <div v-for="option in options" :key="option" :class="rowClass(option)">
          <button
            type="button"
            class="btn w-100 py-3 choicebutton"
            :class="buttonClass(option)"
            @click="checkAnswer(option)"
            :disabled="hasAnswered"
            v-html="option.annotation"
          >
          </button>
        </div>
      </div>
    </div>

    <div v-if="!hasAnswered && allowSkip" class="d-flex flex-column align-items-center mt-3">
      <div
        v-if="!tipStore.isTipShown('skip-notice')" class="alert alert-info py-2 px-3 mb-2 d-flex align-items-center justify-content-between" role="alert" style="font-size: 0.95rem;">
        <span>{{ t('game.skip-notice', 'Already know this? Use skip to move on.') }}</span>
        <button type="button" class="btn-close ms-2" aria-label="Close" @click="tipStore.markTipShown('skip-notice')"></button>
      </div>
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="emit('skip-concept')"
        :disabled="hasAnswered"
      >
        <i class="bi bi-skip-forward me-2"></i>
        {{ t('game.skip', 'Skip') }}
      </button>
    </div>

  </div>
</template>

<style scoped>
.game-container {
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 1rem;
}

.word-to-translate {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
}
.btn-outline-primary-cs{

}
.options-grid button {
  font-size: 1.1rem;
  transition: all 0.2s ease;
  color: #242f51!important;
  border-color:#242f51!important;
}

.options-grid button:hover:not(:disabled) {
  transform: translateY(-2px);
  color: white !important;
}
.options-grid button.btn-success{
  background-color: #dcfce7;
  border-color: #198754!important;
  color: #198754!important;
  font-weight: 500;
}
.options-grid button.btn-danger{
  background-color: #f8d7da;
  font-weight: 500;
  border-color: #721c24!important;
  color: #721c24!important;
}
.compact-btn-xs {
  padding: 0.3rem 1rem !important;
  margin: -0.2rem;
  font-size: 0.9rem !important;
}

@media (max-width: 576px) {
  .btn-xs {
    padding: 0.3rem 1rem !important;
    margin: -0.2rem;
    font-size: 0.9rem !important;
  }
}
.btn-outline-primary-cs{
  border-color:#242f51 !important;
  color:#242f51!important;
  font-size: 14px;
}
.btn-outline-primary-cs:hover{
  background-color: #242f51!important;
  color:#ffffff!important;
}
.score-display {
  font-size: 1.2rem;
  text-align: center;
}
/* Styles for the popout menu */
.popout-menu {
  z-index: 1000;
    position: fixed;
    background: white;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    transform: translateX(-80%);
    width: 250px;
}

.popout-menu ul {
    list-style: none;
    margin: 0;
    padding: 0;
}

.popout-menu li {
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid #ccc;
  font-size:14px;
  font-weight:500;
}



.popout-menu li:hover {
    background-color: #f1f1f1;
}
.btn-grear{
  background: transparent;
  padding: 5px 10px;
}
.bi {
  color: black;
}
button.choicebutton {
  pointer-events: none;
}
</style>
